/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Renders individual cars grouped by category (CarCategories segment), one card per car,
 * in the same category-section layout as ModelCategoryList.
 */

import { useTranslation } from 'react-i18next';
import type { Car } from '../types';
import { categoryLabel } from '../utils/localized';
import CarCard from './CarCard';

function groupByCategory(cars: Car[], lang: string): [string, Car[]][] {
  const map = new Map<string, Car[]>();
  const order = new Map<string, number>();
  for (const car of cars) {
    const category = categoryLabel(car, lang, 'Other');
    order.set(category, Math.min(order.get(category) ?? 99, car.categoryPosition ?? 99));
    if (!map.has(category)) map.set(category, []);
    map.get(category)!.push(car);
  }
  return Array.from(map.entries()).sort(
    (a, b) => (order.get(a[0]) ?? 99) - (order.get(b[0]) ?? 99) || a[0].localeCompare(b[0]),
  );
}

export default function CarCategoryList({ cars }: { cars: Car[] }) {
  const { i18n } = useTranslation();
  const groups = groupByCategory(cars, i18n.language);

  return (
    <div className="space-y-12">
      {groups.map(([category, list]) => (
        <div key={category} className="space-y-5">
          <h3 className="text-2xl font-bold text-gray-900">
            {category} <span className="text-gray-400 font-normal text-lg">· {list.length}</span>
          </h3>

          <div className="overflow-x-auto pb-3 -mx-1 px-1 model-cards-scroll">
            <div className="flex gap-5" style={{ minWidth: 'max-content' }}>
              {list.map((car) => (
                <div key={car.id} className="flex-shrink-0" style={{ width: 240, minWidth: 240 }}>
                  <CarCard car={car} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
